import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  MapPinIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  DocumentArrowDownIcon,
  TrashIcon,
  PencilIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  ChatBubbleBottomCenterTextIcon,
  PaperClipIcon,
  FunnelIcon,
  PlusIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import ConvocationModal from '../components/ConvocationModal';
import PouvoirsRecusModal from '../components/PouvoirsRecusModal';
import AdminAjouterPouvoirModal from '../components/AdminAjouterPouvoirModal';
import FileDropzone from '../components/common/FileDropzone';
import { isAssembleeGenerale, getReunionTypeLabel, getConvocationButtonLabel, buildProcesVerbalFileName, parseContentDispositionFilename } from '../utils/reunionTypes';
import { exportPresencesToExcel, formatRepresenteStatut, formatPouvoirsRecusLabel, formatNomPrenomDisplay, getPresenceFonctionLabel, sortPresencesByFonctionAndName } from '../utils/excelExport';

const PRESENCE_STATUT_OPTIONS = [
  { value: 'present', label: 'Présents' },
  { value: 'absent', label: 'Absents' },
  { value: 'represente', label: 'Représentés' },
  { value: 'excuse', label: 'Excusés' },
];

export default function ReunionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, permissions } = useAuth();
  const [reunion, setReunion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingPieceJointe, setUploadingPieceJointe] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [editingOrdresDuJour, setEditingOrdresDuJour] = useState(false);
  const [ordresDuJourValue, setOrdresDuJourValue] = useState('');
  const [savingOrdresDuJour, setSavingOrdresDuJour] = useState(false);
  const [editingTexteLibre, setEditingTexteLibre] = useState(false);
  const [texteLibreValue, setTexteLibreValue] = useState('');
  const [savingTexteLibre, setSavingTexteLibre] = useState(false);
  const [generatingEmargement, setGeneratingEmargement] = useState(false);
  const [downloadingProcesVerbal, setDownloadingProcesVerbal] = useState(false);
  const [showConvocationModal, setShowConvocationModal] = useState(false);
  const [pouvoirsModal, setPouvoirsModal] = useState(null);
  const [showAjouterPouvoirModal, setShowAjouterPouvoirModal] = useState(false);
  const [showAnneesAdhesionFilter, setShowAnneesAdhesionFilter] = useState(false);
  const [showStatutsFilter, setShowStatutsFilter] = useState(false);
  const [savingPresence, setSavingPresence] = useState(null);
  const [presenceFilters, setPresenceFilters] = useState({
    annees: [],
    statuts: [],
    pouvoirsRecus: false,
    actifsJourAG: false,
    search: '',
  });

  const canConvoquer = user?.role === 'super_admin' || permissions?.reunions?.convoquer;
  const canEditReunion = user?.role === 'super_admin' || permissions?.reunions?.editReunion;
  const canManageDocuments =
    user?.role === 'super_admin'
    || permissions?.reunions?.editReunion
    || permissions?.reunions?.manageDocuments;

  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) {
      navigate('/');
      return;
    }
    
    if (user.role === 'admin' && !permissions?.reunions?.access) {
      navigate('/admin/dashboard');
      return;
    }

    fetchReunion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, permissions, navigate]);

  const fetchReunion = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReunion(response.data);
      setNotesValue(response.data.notes || '');
      setOrdresDuJourValue(response.data.ordresDuJour || '');
      setTexteLibreValue(response.data.texteLibreConvocation || '');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement de la réunion');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const getPresenceUserId = (presence) => {
    if (!presence?.user) return null;
    return typeof presence.user === 'object' ? presence.user._id : presence.user;
  };

  const getTypeLabel = (type) => getReunionTypeLabel(type, reunion?.organisme);

  const getTypeBadgeColor = (type) => {
    return isAssembleeGenerale(type)
      ? 'bg-purple-100 text-purple-800' 
      : 'bg-blue-100 text-blue-800';
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingDoc(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('document', file);
      
      await axios.post(
        `${process.env.REACT_APP_API_URL}/reunions/${id}/documents`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      fetchReunion();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de l\'upload');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleDownloadDocument = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/${id}/documents/${documentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.url, '_blank');
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du téléchargement');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/reunions/${id}/documents/${documentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchReunion();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleUploadPieceJointe = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPieceJointe(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('pieceJointe', file);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/reunions/${id}/pieces-jointes`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      fetchReunion();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de l\'upload');
    } finally {
      setUploadingPieceJointe(false);
      e.target.value = '';
    }
  };

  const handleDownloadPieceJointe = async (pieceJointeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/${id}/pieces-jointes/${pieceJointeId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.url, '_blank');
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du téléchargement');
    }
  };

  const handleDeletePieceJointe = async (pieceJointeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette pièce jointe ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/reunions/${id}/pieces-jointes/${pieceJointeId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchReunion();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/reunions/${id}`,
        { notes: notesValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReunion({ ...reunion, notes: notesValue });
      setEditingNotes(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveOrdresDuJour = async () => {
    setSavingOrdresDuJour(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/reunions/${id}`,
        { ordresDuJour: ordresDuJourValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReunion({ ...reunion, ordresDuJour: ordresDuJourValue });
      setEditingOrdresDuJour(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingOrdresDuJour(false);
    }
  };

  const handleSaveTexteLibre = async () => {
    setSavingTexteLibre(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/reunions/${id}`,
        { texteLibreConvocation: texteLibreValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReunion({ ...reunion, texteLibreConvocation: texteLibreValue });
      setEditingTexteLibre(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingTexteLibre(false);
    }
  };

  const hasEmargement = reunion?.documents?.some(doc => doc.nom === "Feuille d'émargement");
  const isReadOnly = !canEditReunion;
  const isAG = isAssembleeGenerale(reunion?.type);

  const documentsListCount =
    (reunion?.documents?.length || 0)
    + (reunion?.convocationPdf?.key ? 1 : 0)
    + (reunion?.pouvoirsDocuments?.length || 0);

  const canAjouterPouvoir = useMemo(() => {
    if (!reunion || isReadOnly || !reunion.dateConvocation) return false;
    return true;
  }, [reunion, isReadOnly]);

  const normalizePresenceSearch = (value) =>
    (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');

  const hasActivePresenceFilters = useMemo(
    () =>
      presenceFilters.annees.length > 0
      || presenceFilters.statuts.length > 0
      || presenceFilters.pouvoirsRecus
      || presenceFilters.actifsJourAG
      || presenceFilters.search.trim().length > 0,
    [presenceFilters]
  );

  const filteredPresences = useMemo(() => {
    if (!reunion?.presences) return [];
    let list = reunion.presences;
    const searchQuery = presenceFilters.search.trim();
    if (searchQuery) {
      const q = normalizePresenceSearch(searchQuery);
      list = list.filter((p) => {
        const nom = normalizePresenceSearch(p.nom);
        const prenom = normalizePresenceSearch(p.prenom);
        const fullName = `${prenom} ${nom}`.trim();
        const fullNameReverse = `${nom} ${prenom}`.trim();
        return (
          nom.includes(q)
          || prenom.includes(q)
          || fullName.includes(q)
          || fullNameReverse.includes(q)
        );
      });
    }
    if (presenceFilters.annees.length > 0) {
      list = list.filter((p) =>
        p.anneesAdhesion?.some((y) => presenceFilters.annees.includes(y))
      );
    }
    if (presenceFilters.statuts.length > 0) {
      list = list.filter((p) => presenceFilters.statuts.includes(p.statut));
    }
    if (presenceFilters.pouvoirsRecus) {
      list = list.filter((p) => (p.pouvoirsRecus || 0) > 0);
    }
    if (presenceFilters.actifsJourAG) {
      list = list.filter((p) => p.actifAuJourAG);
    }
    return sortPresencesByFonctionAndName(list);
  }, [reunion?.presences, presenceFilters]);

  const togglePresenceAnneeFilter = (annee) => {
    setPresenceFilters((prev) => ({
      ...prev,
      annees: prev.annees.includes(annee)
        ? prev.annees.filter((y) => y !== annee)
        : [...prev.annees, annee].sort((a, b) => b - a),
    }));
  };

  const togglePresenceStatutFilter = (statut) => {
    setPresenceFilters((prev) => ({
      ...prev,
      statuts: prev.statuts.includes(statut)
        ? prev.statuts.filter((s) => s !== statut)
        : [...prev.statuts, statut],
    }));
  };

  const clearStatutsFilter = () => {
    setPresenceFilters((prev) => ({ ...prev, statuts: [] }));
  };

  const clearAnneesAdhesionFilter = () => {
    setPresenceFilters((prev) => ({ ...prev, annees: [] }));
  };

  const resetPresenceFilters = () => {
    setShowAnneesAdhesionFilter(false);
    setShowStatutsFilter(false);
    setPresenceFilters({
      annees: [],
      statuts: [],
      pouvoirsRecus: false,
      actifsJourAG: false,
      search: '',
    });
  };

  const handleUpdatePresence = async (presenceId, newStatut) => {
    setSavingPresence(presenceId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/reunions/${id}/presences/${presenceId}`,
        { statut: newStatut },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Mettre à jour localement
      setReunion(prev => ({
        ...prev,
        presences: prev.presences.map(p => 
          p._id === presenceId ? { ...p, statut: newStatut } : p
        )
      }));
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSavingPresence(null);
    }
  };

  const handleExportPresences = () => {
    if (!reunion || filteredPresences.length === 0) {
      alert('Aucune présence à exporter');
      return;
    }
    exportPresencesToExcel({
      presences: filteredPresences,
      reunion,
      isAG,
    });
  };
  
  // Vérifier si la date de la réunion est passée
  const isReunionPassed = () => {
    if (!reunion?.date) return false;
    const reunionDate = new Date(reunion.date);
    const today = new Date();
    reunionDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return reunionDate < today;
  };

  const handleGenerateEmargement = async () => {
    if (hasEmargement) {
      alert('Une feuille d\'émargement existe déjà');
      return;
    }

    setGeneratingEmargement(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reunions/${id}/emargement`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReunion(response.data.reunion);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la génération');
    } finally {
      setGeneratingEmargement(false);
    }
  };

  const handleDeleteEmargement = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer la feuille d\'émargement ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/reunions/${id}/emargement`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReunion(response.data.reunion);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleDownloadConvocationPdf = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/${id}/convocation-pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.url, '_blank');
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du téléchargement de la convocation');
    }
  };

  const handleDownloadProcesVerbal = async () => {
    setDownloadingProcesVerbal(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/${id}/proces-verbal`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      const disposition = response.headers['content-disposition'] || '';
      const filename =
        parseContentDispositionFilename(disposition)
        || buildProcesVerbalFileName(reunion);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du téléchargement du modèle de procès verbal');
    } finally {
      setDownloadingProcesVerbal(false);
    }
  };

  const handleDownloadPouvoirPdf = async (pouvoirId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/${id}/pouvoirs/${pouvoirId}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.url, '_blank');
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du téléchargement');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !reunion) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-soft p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erreur</h3>
          <p className="text-gray-600 mb-6">{error || 'Réunion introuvable'}</p>
          <button
            onClick={() => navigate('/admin/reunions')}
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <Link
          to="/admin/reunions"
          className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Retour à la liste des réunions
        </Link>
        
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getTypeBadgeColor(reunion.type)}`}>
                {getTypeLabel(reunion.type)}
              </span>
              <span className="inline-flex items-center px-3 py-1 text-sm font-bold rounded bg-gray-100 text-gray-800">
                <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                {reunion.organisme}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CalendarDaysIcon className="h-6 w-6 text-gray-400" />
              {new Date(reunion.date).toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
              {(reunion.heureDebut || reunion.heureFin) && (
                <span className="text-lg font-normal text-gray-600">
                  {reunion.heureDebut && reunion.heureFin 
                    ? `de ${reunion.heureDebut} à ${reunion.heureFin}`
                    : reunion.heureDebut 
                      ? `à ${reunion.heureDebut}`
                      : `jusqu'à ${reunion.heureFin}`
                  }
                </span>
              )}
            </h1>
            <p className="mt-1 text-gray-600 flex items-center gap-1">
              <MapPinIcon className="h-4 w-4" />
              {reunion.lieu}
            </p>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="px-8 py-6">
        {reunion.fermee && (
          <div className="mb-6 p-4 bg-slate-100 border border-slate-300 rounded-lg text-sm text-slate-700 flex flex-wrap items-center justify-between gap-3">
            <span>
              Cette réunion est <strong>clôturée</strong>.
            </span>
            <button
              type="button"
              onClick={handleDownloadProcesVerbal}
              disabled={downloadingProcesVerbal}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-800 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {downloadingProcesVerbal ? 'Téléchargement…' : 'Télécharger le modèle de procès verbal'}
            </button>
          </div>
        )}
        <div className="space-y-6">
          {/* Ligne 1 : Notes | Documents */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

            {/* Section Notes */}
            <div className="bg-white rounded-lg shadow p-6 h-full">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                  Notes
                </h2>
                {!editingNotes && !isReadOnly && (
                  <button
                    onClick={() => setEditingNotes(true)}
                    className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Modifier
                  </button>
                )}
              </div>
              
              {editingNotes ? (
                <div className="space-y-3">
                  <textarea
                    value={notesValue}
                    onChange={(e) => setNotesValue(e.target.value)}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Compte-rendu, remarques..."
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setNotesValue(reunion.notes || '');
                        setEditingNotes(false);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleSaveNotes}
                      disabled={savingNotes}
                      className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
                    >
                      {savingNotes ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="prose prose-sm max-w-none">
                  {reunion.notes ? (
                    <p className="text-gray-700 whitespace-pre-wrap">{reunion.notes}</p>
                  ) : (
                    <p className="text-gray-400 italic">Aucune note pour cette réunion</p>
                  )}
                </div>
              )}
            </div>

            {/* Section Documents */}
            <div className="bg-white rounded-lg shadow p-6 h-full">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                  Documents ({documentsListCount})
                </h2>

                <div className="flex flex-wrap items-center gap-2">
                  {canEditReunion && (
                    <button
                      type="button"
                      onClick={handleDownloadProcesVerbal}
                      disabled={downloadingProcesVerbal}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:bg-gray-400 transition-colors"
                    >
                      {downloadingProcesVerbal ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          Téléchargement...
                        </>
                      ) : (
                        <>
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          Télécharger le modèle de procès verbal
                        </>
                      )}
                    </button>
                  )}
                  {!hasEmargement && !isReunionPassed() && !isReadOnly && (
                    <button
                      onClick={handleGenerateEmargement}
                      disabled={generatingEmargement}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
                    >
                      {generatingEmargement ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Génération...
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentListIcon className="h-4 w-4" />
                          Générer feuille d'émargement
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Liste des documents */}
              <div className="space-y-2 mb-4">
                {reunion.convocationPdf?.key && (
                  <div className="flex items-center justify-between p-3 border rounded-lg border-green-200 bg-green-50">
                    <div className="flex items-center min-w-0">
                      <EnvelopeIcon className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm truncate text-green-900 font-medium">
                          {reunion.convocationPdf.nom || 'Document de convocation'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {reunion.convocationPdf.dateGeneration
                            && new Date(reunion.convocationPdf.dateGeneration).toLocaleDateString('fr-FR')}
                          {' · '}
                          PDF
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleDownloadConvocationPdf}
                      className="p-2 text-green-700 hover:text-green-900 hover:bg-green-100 rounded"
                      title="Télécharger le PDF de convocation"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
                {reunion.documents?.length === 0
                  && !reunion.convocationPdf?.key
                  && !(reunion.pouvoirsDocuments?.length > 0) ? (
                  <p className="text-gray-400 italic text-sm">Aucun document attaché</p>
                ) : (
                  reunion.documents?.map((doc) => {
                    const isEmargement = doc.nom === "Feuille d'émargement";
                    return (
                      <div
                        key={doc._id}
                        className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 ${
                          isEmargement ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center min-w-0">
                          {isEmargement ? (
                            <ClipboardDocumentListIcon className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0" />
                          ) : (
                            <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className={`text-sm truncate ${isEmargement ? 'text-indigo-900 font-medium' : 'text-gray-900'}`}>
                              {isEmargement ? "Feuille d'émargement" : doc.nomOriginal}
                            </p>
                            <p className="text-xs text-gray-500">
                              {doc.dateAjout && new Date(doc.dateAjout).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <button
                            onClick={() => handleDownloadDocument(doc._id)}
                            className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded"
                            title="Télécharger"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                          </button>
                          {!(isEmargement && isReunionPassed())
                            && (isEmargement ? canEditReunion : canManageDocuments) && (
                            <button
                              onClick={() => isEmargement ? handleDeleteEmargement() : handleDeleteDocument(doc._id)}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                              title="Supprimer"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                {reunion.pouvoirsDocuments?.map((pouvoir) => (
                  <div
                    key={pouvoir._id}
                    className="flex items-center justify-between p-3 border rounded-lg border-honey-200 bg-honey-50"
                  >
                    <div className="flex items-center min-w-0">
                      <DocumentTextIcon className="h-5 w-5 text-honey-600 mr-2 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm truncate text-honey-900 font-medium">
                          {pouvoir.nom}
                        </p>
                        <p className="text-xs text-gray-500">
                          {pouvoir.dateAjout
                            && new Date(pouvoir.dateAjout).toLocaleDateString('fr-FR')}
                          {' · '}
                          Mandataire : {pouvoir.mandatairePrenom} {pouvoir.mandataireNom}
                        </p>
                      </div>
                    </div>
                    {pouvoir.hasPdf && (
                      <button
                        type="button"
                        onClick={() => handleDownloadPouvoirPdf(pouvoir._id)}
                        className="p-2 text-honey-700 hover:text-honey-900 hover:bg-honey-100 rounded"
                        title="Télécharger le pouvoir"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {canManageDocuments && (
              <div className="border-t border-gray-200 pt-4">
                <FileDropzone
                  onFileSelect={(file) => handleUploadDocument({ target: { files: [file] } })}
                  uploading={uploadingDoc}
                  label="Ajouter un document"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                />
              </div>
              )}
            </div>

          </div>

          {/* Encart Convocation */}
          <div className="bg-white rounded-lg shadow overflow-hidden border border-indigo-100/80">
            {/* En-tête */}
            <div className="px-6 py-4 bg-indigo-50/60 border-b border-indigo-100 flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <EnvelopeIcon className="h-5 w-5 text-indigo-600" />
                  Convocation
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Contenu repris dans le PDF et l&apos;e-mail envoyés aux convoqués.
                </p>
              </div>
          
              {/* Bouton Convoquer ou mention convocation envoyée */}
              {reunion.dateConvocation ? (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 rounded-lg text-sm">
                  <EnvelopeIcon className="h-4 w-4" />
                  Convocation envoyée le {new Date(reunion.dateConvocation).toLocaleDateString('fr-FR')} à {new Date(reunion.dateConvocation).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              ) : (
                canConvoquer && !isReunionPassed() && !isReadOnly && (
                  <button
                    onClick={() => setShowConvocationModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-sm text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <EnvelopeIcon className="h-5 w-5" />
                    {getConvocationButtonLabel(reunion.type, reunion.organisme)}
                  </button>
                )
              )}
            </div>

            {/* Corps : contenu textuel | pièces jointes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x divide-gray-100">
              {/* Colonne gauche — textes de convocation */}
              <div className="p-6 space-y-8">
                {/* Ordre(s) du jour */}
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                      <ClipboardDocumentListIcon className="h-4 w-4 text-indigo-500" />
                      Ordre du jour
                    </h3>
                    {!editingOrdresDuJour && !isReadOnly && (
                      <button
                        onClick={() => setEditingOrdresDuJour(true)}
                        className="text-indigo-600 hover:text-indigo-800 text-xs flex items-center gap-1"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                        Modifier
                      </button>
                    )}
                  </div>

                  {editingOrdresDuJour ? (
                    <div className="space-y-3">
                      <textarea
                        value={ordresDuJourValue}
                        onChange={(e) => setOrdresDuJourValue(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        placeholder="Points à l'ordre du jour..."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setOrdresDuJourValue(reunion.ordresDuJour || '');
                            setEditingOrdresDuJour(false);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={handleSaveOrdresDuJour}
                          disabled={savingOrdresDuJour}
                          className="flex-1 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 text-sm"
                        >
                          {savingOrdresDuJour ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {reunion.ordresDuJour || (
                        <span className="text-gray-400 italic">Aucun ordre du jour défini</span>
                      )}
                    </div>
                  )}
                </section>

                {/* Texte libre */}
                <section className="pt-8 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                      <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-indigo-500" />
                      Texte libre
                    </h3>
                    {!editingTexteLibre && !isReadOnly && (
                      <button
                        onClick={() => setEditingTexteLibre(true)}
                        className="text-indigo-600 hover:text-indigo-800 text-xs flex items-center gap-1"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                        Modifier
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Complément affiché sous l&apos;ordre du jour dans le PDF et l&apos;e-mail.
                  </p>

                  {editingTexteLibre ? (
                    <div className="space-y-3">
                      <textarea
                        value={texteLibreValue}
                        onChange={(e) => setTexteLibreValue(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        placeholder="Informations complémentaires, consignes d'accès..."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setTexteLibreValue(reunion.texteLibreConvocation || '');
                            setEditingTexteLibre(false);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={handleSaveTexteLibre}
                          disabled={savingTexteLibre}
                          className="flex-1 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 text-sm"
                        >
                          {savingTexteLibre ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {reunion.texteLibreConvocation || (
                        <span className="text-gray-400 italic">Aucun texte libre défini</span>
                      )}
                    </div>
                  )}
                </section>
              </div>

              {/* Colonne droite — pièces jointes e-mail */}
              <div className="p-6 bg-gray-50/40">
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2 mb-1">
                    <PaperClipIcon className="h-4 w-4 text-honey-600" />
                    Pièces jointes e-mail
                    <span className="font-normal normal-case text-gray-500">
                      ({reunion.piecesJointes?.length || 0}/5)
                    </span>
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Fichiers joints automatiquement à l&apos;e-mail de convocation (en plus du PDF de convocation).
                  </p>

                  <div className="space-y-2 mb-4">
                    {!reunion.piecesJointes?.length ? (
                      <p className="text-gray-400 italic text-sm">Aucune pièce jointe</p>
                    ) : (
                      reunion.piecesJointes.map((pj) => (
                        <div
                          key={pj._id}
                          className="flex items-center justify-between p-3 border rounded-lg bg-white border-honey-200/80 hover:bg-honey-50/30 transition-colors"
                        >
                          <div className="flex items-center min-w-0">
                            <PaperClipIcon className="h-4 w-4 text-honey-600 mr-2 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm truncate text-gray-900">{pj.nomOriginal}</p>
                              <p className="text-xs text-gray-500">
                                {pj.dateAjout && new Date(pj.dateAjout).toLocaleDateString('fr-FR')}
                                {pj.taille ? ` · ${(pj.taille / 1024).toFixed(0)} Ko` : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            <button
                              type="button"
                              onClick={() => handleDownloadPieceJointe(pj._id)}
                              className="p-1.5 text-honey-700 hover:bg-honey-100 rounded"
                              title="Télécharger"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                            {!isReadOnly && (
                              <button
                                type="button"
                                onClick={() => handleDeletePieceJointe(pj._id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                title="Supprimer"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {(reunion.piecesJointes?.length || 0) < 5 && !isReadOnly && (
                    <FileDropzone
                      onFileSelect={(file) => handleUploadPieceJointe({ target: { files: [file] } })}
                      uploading={uploadingPieceJointe}
                      label="Ajouter une pièce jointe"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                    />
                  )}
                </section>
              </div>
            </div>
          </div>
        </div>
        {/* Section Présences */}
        {reunion.presences && reunion.presences.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5 text-gray-400" />
                Présences ({reunion.presences.filter(p => p.statut === 'present').length}/{reunion.presences.length})
              </h2>
              <div className="flex items-center flex-wrap gap-4">
                <div className="flex items-center flex-wrap gap-4 text-sm">
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircleIcon className="h-4 w-4" />
                    {reunion.presences.filter(p => p.statut === 'present').length} présent(s)
                  </span>
                  <span className="flex items-center gap-1 text-indigo-600">
                    <UserGroupIcon className="h-4 w-4" />
                    {reunion.presences.filter(p => p.statut === 'represente').length} représenté(s)
                  </span>
                  {!isAG && (
                    <span className="flex items-center gap-1 text-yellow-600">
                      <MinusCircleIcon className="h-4 w-4" />
                      {reunion.presences.filter(p => p.statut === 'excuse').length} excusé(s)
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-red-600">
                    <XCircleIcon className="h-4 w-4" />
                    {reunion.presences.filter(p => p.statut === 'absent').length} absent(s)
                  </span>
                </div>
                {canAjouterPouvoir && (
                  <button
                    type="button"
                    onClick={() => setShowAjouterPouvoirModal(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-honey-700 bg-honey-50 border border-honey-200 rounded-lg hover:bg-honey-100 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Ajouter un pouvoir
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleExportPresences}
                  disabled={filteredPresences.length === 0}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-1.5" />
                  Exporter Excel
                </button>
              </div>
            </div>

            <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                    <FunnelIcon className="h-4 w-4" />
                    Filtres
                  </p>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>
                      {filteredPresences.length} affiché{filteredPresences.length > 1 ? 's' : ''}
                      {' '}sur {reunion.presences.length}
                    </span>
                    {hasActivePresenceFilters && (
                      <button
                        type="button"
                        onClick={resetPresenceFilters}
                        className="text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        Réinitialiser
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                  <div className="min-w-[200px] flex-1 max-w-xs">
                    <label htmlFor="presence-search" className="sr-only">
                      Rechercher par nom ou prénom
                    </label>
                    <input
                      id="presence-search"
                      type="search"
                      value={presenceFilters.search}
                      onChange={(e) =>
                        setPresenceFilters((prev) => ({ ...prev, search: e.target.value }))
                      }
                      placeholder="Rechercher nom ou prénom…"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  {reunion.anneesAdhesionDisponibles?.length > 0 && (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowAnneesAdhesionFilter(!showAnneesAdhesionFilter)}
                        className="inline-flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors"
                      >
                        Année d&apos;adhésion
                        {presenceFilters.annees.length > 0 && (
                          <span className="ml-2 px-2 py-0.5 bg-honey-500 rounded-full text-xs">
                            {presenceFilters.annees.length}
                          </span>
                        )}
                      </button>

                      {showAnneesAdhesionFilter && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setShowAnneesAdhesionFilter(false)}
                          />
                          <div className="absolute z-20 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200">
                            <div className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-semibold text-gray-900">
                                  Année d&apos;adhésion
                                </span>
                                {presenceFilters.annees.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={clearAnneesAdhesionFilter}
                                    className="text-sm text-indigo-600 hover:text-indigo-700"
                                  >
                                    Effacer
                                  </button>
                                )}
                              </div>
                              <div className="space-y-2 max-h-60 overflow-y-auto">
                                {reunion.anneesAdhesionDisponibles.map((annee) => (
                                  <button
                                    key={annee}
                                    type="button"
                                    onClick={() => togglePresenceAnneeFilter(annee)}
                                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                                      presenceFilters.annees.includes(annee)
                                        ? 'bg-honey-50 text-honey-700'
                                        : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                  >
                                    <span>{annee}</span>
                                    {presenceFilters.annees.includes(annee) && (
                                      <CheckIcon className="h-4 w-4 text-honey-600" />
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowStatutsFilter(!showStatutsFilter)}
                      className="inline-flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors"
                    >
                      Statut
                      {presenceFilters.statuts.length > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-honey-500 rounded-full text-xs">
                          {presenceFilters.statuts.length}
                        </span>
                      )}
                    </button>

                    {showStatutsFilter && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowStatutsFilter(false)}
                        />
                        <div className="absolute z-20 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200">
                          <div className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-semibold text-gray-900">
                                Statut de présence
                              </span>
                              {presenceFilters.statuts.length > 0 && (
                                <button
                                  type="button"
                                  onClick={clearStatutsFilter}
                                  className="text-sm text-indigo-600 hover:text-indigo-700"
                                >
                                  Effacer
                                </button>
                              )}
                            </div>
                            <div className="space-y-2">
                              {PRESENCE_STATUT_OPTIONS.map(({ value, label }) => (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() => togglePresenceStatutFilter(value)}
                                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                                    presenceFilters.statuts.includes(value)
                                      ? 'bg-honey-50 text-honey-700'
                                      : 'hover:bg-gray-50 text-gray-700'
                                  }`}
                                >
                                  <span>{label}</span>
                                  {presenceFilters.statuts.includes(value) && (
                                    <CheckIcon className="h-4 w-4 text-honey-600" />
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={presenceFilters.pouvoirsRecus}
                      onChange={(e) =>
                        setPresenceFilters((prev) => ({ ...prev, pouvoirsRecus: e.target.checked }))
                      }
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Pouvoirs reçus
                  </label>
                  {isAG && (
                    <label
                      className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                      title="Adhésion actif ou expirée de l'année civile de l'AG, enregistrée avant minuit J+1"
                    >
                      <input
                        type="checkbox"
                        checked={presenceFilters.actifsJourAG}
                        onChange={(e) =>
                          setPresenceFilters((prev) => ({ ...prev, actifsJourAG: e.target.checked }))
                        }
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      Actifs au jour de l&apos;AG
                    </label>
                  )}
                </div>
              </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Membre</th>
                    {isAG && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Année(s)
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fonction</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Pouvoirs</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPresences.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isAG ? 6 : 5}
                        className="px-4 py-8 text-center text-sm text-gray-400 italic"
                      >
                        Aucun membre ne correspond aux filtres sélectionnés.
                      </td>
                    </tr>
                  ) : (
                  filteredPresences.map((presence) => (
                    <tr key={presence._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {formatNomPrenomDisplay(presence.nom, presence.prenom) || '—'}
                        </div>
                        {presence.email && (
                          <div className="text-sm text-gray-500">{presence.email}</div>
                        )}
                      </td>
                      {isAG && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {presence.anneesAdhesion?.length
                            ? presence.anneesAdhesion.join(', ')
                            : '—'}
                          {presence.actifAuJourAG && (
                            <span
                              className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                              title="Actif au jour de l'AG"
                            >
                              Actif
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {getPresenceFonctionLabel(presence)}
                      </td>
                      <td className="px-4 py-3 text-center text-sm font-medium max-w-xs">
                        {(presence.pouvoirsRecus || 0) > 0 ? (
                          <button
                            type="button"
                            onClick={() =>
                              setPouvoirsModal({
                                userId: getPresenceUserId(presence),
                                prenom: presence.prenom,
                                nom: presence.nom,
                              })
                            }
                            className="text-indigo-600 hover:text-indigo-800 hover:underline text-left"
                            title="Voir le détail des pouvoirs reçus"
                          >
                            {formatPouvoirsRecusLabel(presence)}
                          </button>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {presence.statut === 'represente' ? (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 max-w-[14rem] text-center leading-snug">
                            {formatRepresenteStatut(presence)}
                          </span>
                        ) : (
                          <div className="flex items-center justify-center gap-1">
                            {savingPresence === presence._id ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                            ) : isReadOnly ? (
                              <span className="text-xs text-gray-500 capitalize">{presence.statut}</span>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleUpdatePresence(presence._id, 'present')}
                                  className={`p-1.5 rounded-full transition-colors ${
                                    presence.statut === 'present'
                                      ? 'bg-green-100 text-green-600'
                                      : 'text-gray-400 hover:bg-green-50 hover:text-green-600'
                                  }`}
                                  title="Présent"
                                >
                                  <CheckCircleIcon className="h-5 w-5" />
                                </button>
                                {!isAG && (
                                  <button
                                    onClick={() => handleUpdatePresence(presence._id, 'excuse')}
                                    className={`p-1.5 rounded-full transition-colors ${
                                      presence.statut === 'excuse'
                                        ? 'bg-yellow-100 text-yellow-600'
                                        : 'text-gray-400 hover:bg-yellow-50 hover:text-yellow-600'
                                    }`}
                                    title="Excusé"
                                  >
                                    <MinusCircleIcon className="h-5 w-5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleUpdatePresence(presence._id, 'absent')}
                                  className={`p-1.5 rounded-full transition-colors ${
                                    presence.statut === 'absent'
                                      ? 'bg-red-100 text-red-600'
                                      : 'text-gray-400 hover:bg-red-50 hover:text-red-600'
                                  }`}
                                  title="Absent"
                                >
                                  <XCircleIcon className="h-5 w-5" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {presence.pouvoirId && (
                          <button
                            onClick={() => handleDownloadPouvoirPdf(presence.pouvoirId)}
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded"
                            title="Télécharger le pouvoir"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                  )}
                </tbody>
              </table>
            </div>

            {reunion.pouvoirFootnote && (
              <p className="mt-4 text-xs text-gray-500 italic border-t pt-3">
                {reunion.pouvoirFootnote}
              </p>
            )}
          </div>
        )}

        {/* Informations supplémentaires */}
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Créée par</p>
              <p className="text-gray-900 font-medium">
                {reunion.creePar?.prenom} {reunion.creePar?.nom}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Date de création</p>
              <p className="text-gray-900 font-medium">
                {new Date(reunion.createdAt).toLocaleDateString('fr-FR')}
              </p>
            </div>
            {reunion.modifiePar && (
              <>
                <div>
                  <p className="text-gray-500">Modifiée par</p>
                  <p className="text-gray-900 font-medium">
                    {reunion.modifiePar?.prenom} {reunion.modifiePar?.nom}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Dernière modification</p>
                  <p className="text-gray-900 font-medium">
                    {new Date(reunion.updatedAt).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal pouvoirs reçus */}
      <PouvoirsRecusModal
        isOpen={Boolean(pouvoirsModal)}
        onClose={() => setPouvoirsModal(null)}
        reunionId={id}
        mandataire={pouvoirsModal}
        isReadOnly={isReadOnly}
        onPouvoirCancelled={() => fetchReunion(true)}
        onDownloadPdf={handleDownloadPouvoirPdf}
      />

      <AdminAjouterPouvoirModal
        isOpen={showAjouterPouvoirModal}
        onClose={() => setShowAjouterPouvoirModal(false)}
        reunionId={id}
        onSuccess={() => fetchReunion(true)}
      />

      {/* Modal de convocation */}
      <ConvocationModal
        isOpen={showConvocationModal}
        onClose={() => setShowConvocationModal(false)}
        reunionId={id}
        reunionType={reunion.type}
        reunionOrganisme={reunion.organisme}
        reunionDate={reunion.date}
        storedPiecesJointes={reunion.piecesJointes || []}
        onSuccess={(updatedReunion) => setReunion(updatedReunion)}
      />
    </div>
  );
}
